// Share path with graceful degradation (design §6.4): navigator.share where it
// exists, clipboard + toast everywhere else. WeChat JS-SDK is out of scope for
// sprint 1 (TECH_SPEC §6).

import { t } from '../i18n';
import { toast } from './dom';

export type ShareChannel = 'webshare' | 'clipboard' | 'failed';

export interface SharePayload {
  url: string;
  text: string;
  title: string;
}

/** Must be called directly from a click handler to keep user activation. */
export async function shareRun(root: HTMLElement, payload: SharePayload): Promise<ShareChannel> {
  const nav = navigator as Navigator & {
    share?: (data: ShareData) => Promise<void>;
  };
  if (typeof nav.share === 'function') {
    try {
      await nav.share({ title: payload.title, text: payload.text, url: payload.url });
      return 'webshare';
    } catch (err) {
      // AbortError = user dismissed the sheet; don't fall through to clipboard.
      if (err instanceof DOMException && err.name === 'AbortError') return 'failed';
    }
  }

  const line = `${payload.text} ${payload.url}`;
  try {
    await navigator.clipboard.writeText(line);
    toast(root, t('share.copied'));
    return 'clipboard';
  } catch {
    if (legacyCopy(line)) {
      toast(root, t('share.copied'));
      return 'clipboard';
    }
    toast(root, t('share.failed'));
    return 'failed';
  }
}

function legacyCopy(text: string): boolean {
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.append(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}
